import uuidv1 from 'uuid/v1'
import { FILTER_OPTIONS } from '@/constants/custom-pages'

const FORM_FIELDS = {
  showFilter: false,
  filterTypes: [],
  filterBlocks: [],
}

const getDefaultState = () => ({
  selectedLanguage: 'ru',
  form: {
    ru: {
      showFilter: false,
      filterTypes: [],
      filterBlocks: [],
    },
  },
  filterOptions: {
    ru: [...FILTER_OPTIONS],
  },
  isFilterModalOpened: false,
  filterEditData: null,
})

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_SELECTED_LANGUAGE(state, lang) {
      state.selectedLanguage = lang
    },
    SET_FORM(state, form) {
      state.form = JSON.parse(JSON.stringify(form))
    },
    SET_FILTER_OPTIONS(state, options) {
      state.filterOptions = options
    },
    SET_FILTER_MODAL_OPENED(state, val) {
      state.isFilterModalOpened = val
    },
    SET_FILTER_EDIT_DATA(state, val) {
      state.filterEditData = val
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    setSelectedLanguage({ commit }, lang) {
      commit('SET_SELECTED_LANGUAGE', lang)
    },
    onFilterModalClose({ commit }) {
      commit('SET_FILTER_MODAL_OPENED', false)
      commit('SET_FILTER_EDIT_DATA', null)
    },
    initForm({ commit, rootGetters }) {
      const langs = rootGetters['langs']?.langData

      if (langs.length === 0) {
        commit('SET_FORM', {
          ru: { ...FORM_FIELDS },
        })

        return
      }

      const form = langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: { ...FORM_FIELDS },
        }
      }, {})

      const filterOptions = langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: [...FILTER_OPTIONS],
        }
      }, {})

      commit('SET_FORM', form)
      commit('SET_FILTER_OPTIONS', filterOptions)
    },
    async fillFilters({ commit, state, dispatch, rootGetters }, data) {
      await dispatch('getLangs', null, { root: true })

      const langs = rootGetters['langs']?.langData
      const selectedMatches = rootGetters['content/selectedMatches']

      const filterOptions = JSON.parse(JSON.stringify(state.filterOptions))

      const copyFilterForm = JSON.parse(JSON.stringify(state.form))
      const filters = {}

      data.filters.forEach((filter) => {
        const blockId = uuidv1()

        // eslint-disable-next-line no-unused-expressions
        filter.langs?.forEach((content) => {
          const langName = langs.find((l) => content.lid === l.id)?.name

          if (!filters[langName]) {
            filters[langName] = {
              showFilter: true,
              filterTypes: [],
              filterBlocks: [],
            }
          }

          if (!filterOptions[langName]) {
            filterOptions[langName] = []
          }

          const matches = selectedMatches?.[langName] ?? []

          if (filter.type === 'custom') {
            filters[langName].filterTypes.push(blockId)
            filterOptions[langName].push({
              label: content.title,
              value: blockId,
              children: content.matches?.map((m) => {
                const fullMatch = matches?.find((fullM) => fullM.id === m) ?? {}

                return { ...fullMatch }
              }),
              ...(filter.type === 'custom' && { custom: true }),
            })
          } else {
            filters[langName].filterTypes.push(filter.type)
          }

          filters[langName].filterBlocks.push({
            id: filter.type === 'custom' ? blockId : filter.type,
            title: content.title,
            children: content.matches,
            ...(filter.type === 'custom' && { custom: true }),
          })
        })
      })

      if (Object.values(filters).length) {
        commit('SET_FORM', { ...copyFilterForm, ...filters })
      }

      if (Object.values(filterOptions).length) {
        commit('SET_FILTER_OPTIONS', filterOptions)
      }
    },
    removeMatchFromFilters({ commit, state }, block) {
      const formCopy = JSON.parse(JSON.stringify(state.form))
      const filterOptionsCopy = JSON.parse(JSON.stringify(state.filterOptions))

      const matchIdsToRemove = []

      if (block.type === 'match') {
        matchIdsToRemove.push(block.id)
      }

      if (block.type === 'tournament') {
        const matches = block.children.map(m => m.id)

        matchIdsToRemove.push(...matches)
      }

      if (!matchIdsToRemove.length) {
        return
      }

      // update filterOptions
      Object.entries(filterOptionsCopy).forEach(([lang, options]) => {
        filterOptionsCopy[lang] = options.filter(opt => {
          if (!opt.custom) return true

          opt.children = opt.children.filter(m => !matchIdsToRemove.includes(m.id))
          return opt.children.length > 0
        })
      })

      // update form
      Object.entries(formCopy).forEach(([lang, formLang]) => {
        formLang.filterBlocks = formLang.filterBlocks.filter(block => {
          if (!block.custom) return true

          block.children = block.children.filter(id => !matchIdsToRemove.includes(id))
          return block.children.length > 0
        })

        formLang.filterTypes = formLang.filterBlocks.map(b => b.id.toString())
      })

      commit('SET_FORM', formCopy)
      commit('SET_FILTER_OPTIONS', filterOptionsCopy)
    },
    onFilterTypeSelect({ commit, getters, state }, id) {
      const { currentFilterOptions } = getters

      const foundType = currentFilterOptions.find(
        (option) => option.value === id,
      )

      if (!foundType) {
        return
      }

      const formCopy = JSON.parse(JSON.stringify(state.form))

      formCopy[state.selectedLanguage].filterBlocks.push({
        id: foundType.value ?? id,
        title: foundType?.label,
        children: foundType?.children?.map((m) => m.id) ?? null,
        custom: foundType?.custom,
      })

      commit('SET_FORM', formCopy)
    },
    onFilterTypeDeselect({ commit, state }, id) {
      const formCopy = JSON.parse(JSON.stringify(state.form))

      const found = formCopy[state.selectedLanguage].filterBlocks.findIndex(
        (block) => block.id === id,
      )

      if (found > -1) {
        formCopy[state.selectedLanguage].filterBlocks.splice(found, 1)

        commit('SET_FORM', formCopy)
      }
    },
    onFilterBlockRemove({ commit, state }, id) {
      const formCopy = JSON.parse(JSON.stringify(state.form))

      const foundIndex = formCopy[state.selectedLanguage].filterTypes.findIndex(
        (type) => type === id,
      )

      if (foundIndex > -1) {
        formCopy[state.selectedLanguage].filterTypes.splice(foundIndex, 1)

        commit('SET_FORM', formCopy)
      }
    },
    onCustomFilterSubmit({ commit, state }, { data, id }) {
      const filterId = id || uuidv1()

      const formCopy = JSON.parse(JSON.stringify(state.form))
      const filterOptionsCopy = JSON.parse(JSON.stringify(state.filterOptions))

      Object.entries(data).forEach(([lang, value]) => {
        const options = filterOptionsCopy[lang]
        const existingOption = options.find((opt) => opt.value === filterId)

        if (existingOption) {
          existingOption.label = value.title
          existingOption.data = [...value.matches]
        } else {
          options.push({
            label: value.title,
            value: filterId,
            children: [...value.matches],
            custom: true,
          })
        }

        const formLang = formCopy[lang]
        const existingBlock = formLang.filterBlocks.find(
          (block) => block.id === filterId,
        )

        if (existingBlock) {
          existingBlock.title = value.title
          existingBlock.children = value.matches.map(m => m.id)
        } else {
          formLang.filterTypes.push(filterId.toString())
          formLang.filterBlocks.push({
            id: filterId,
            title: value.title,
            children: value.matches.map(m => m.id),
            custom: true,
          })
        }
      })

      commit('SET_FORM', formCopy)
      commit('SET_FILTER_OPTIONS', filterOptionsCopy)
      commit('SET_FILTER_MODAL_OPENED', false)
    },
    onCustomFilterEdit({ commit, rootGetters, state }, id) {
      const langs = rootGetters['langs']?.langData

      const formCopy = JSON.parse(JSON.stringify(state.form))

      const customFilter = langs.reduce((acc, lang) => {
        const foundBlock = formCopy?.[lang.name]?.filterBlocks?.find(
          (f) => f.id === id,
        )

        if (foundBlock) {
          return {
            ...acc,
            [lang.name]: {
              title: foundBlock.title,
              id: foundBlock.id,
              matches: foundBlock.children,
            },
          }
        }

        return {
          ...acc,
        }
      }, {})

      commit('SET_FILTER_EDIT_DATA', customFilter)
      commit('SET_FILTER_MODAL_OPENED', true)
    },
  },
  getters: {
    selectedLanguage: (state) => state.selectedLanguage,
    isFilterModalOpened: (state) => state.isFilterModalOpened,
    filterEditData: (state) => state.filterEditData,
    currentForm: (state) => state.form[state.selectedLanguage] || {},
    currentFilterOptions: (state) =>
      state.filterOptions[state.selectedLanguage] || [],
  },
}
